import { useEffect, useState } from 'react';
import { Loader2, Check, X, ShieldAlert, RefreshCw, ExternalLink, Clock, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import {
  adminListDirectoryApprovals,
  adminGetDirectoryApproval,
  adminApproveDirectory,
  adminRejectDirectory,
  adminSuspendDirectory,
  adminDirectoryPhotoUrl,
  adminDirectoryHeaderUrl,
  adminDirectoryGalleryUrl,
} from '../../services/api';

const STATUSES = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'draft', label: 'Draft' },
];

const StatusPill = ({ status }) => {
  const map = {
    approved: 'bg-emerald-100 text-emerald-800',
    pending: 'bg-blue-100 text-blue-800',
    rejected: 'bg-red-100 text-red-800',
    suspended: 'bg-red-200 text-red-900',
    draft: 'bg-gray-100 text-gray-700',
  };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${map[status] || 'bg-gray-100'}`}>{status}</span>;
};

const DiffRow = ({ label, draft, approved }) => {
  const changed = JSON.stringify(draft) !== JSON.stringify(approved);
  const fmt = (v) => Array.isArray(v) ? (v.join(', ') || '—') : (v === '' || v == null ? '—' : String(v));
  return (
    <div className={`p-2 rounded ${changed ? 'bg-amber-50 border border-amber-200' : ''}`}>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{label}{changed && <span className="ml-2 text-amber-700 font-semibold">changed</span>}</div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] text-gray-400">Draft</div>
          <div className={changed ? 'text-amber-900 font-medium' : 'text-gray-700'}>{fmt(draft)}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400">Approved</div>
          <div className="text-gray-500">{fmt(approved)}</div>
        </div>
      </div>
    </div>
  );
};

const ReviewDialog = ({ id, onClose, onDone }) => {
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminGetDirectoryApproval(id);
        setDriver(data);
      } catch { toast.error('Failed to load profile.'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const doAction = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally { setBusy(false); }
  };

  if (loading) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl"><div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></DialogContent>
    </Dialog>
  );
  if (!driver) return null;
  const draft = driver.draft_content || {};
  const approved = driver.approved_content || {};

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="directory-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Reviewing: {driver.business_name || driver.name}
              <StatusPill status={driver.directory_status} />
              {driver.has_pending_changes && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">Pending changes</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Header preview — admin passthrough works even for
                first-time PENDING drivers who have no approved snapshot
                yet, so reviewers can actually see what they uploaded. */}
            <div className="relative rounded overflow-hidden bg-gray-100 border border-gray-200 aspect-[16/5]">
              {driver.has_header_image && (
                <img
                  src={adminDirectoryHeaderUrl(id)}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              <div className="absolute -bottom-6 left-4">
                <div className="w-16 h-16 rounded-full border-4 border-white shadow bg-gray-100 overflow-hidden">
                  {driver.has_profile_image && (
                    <img
                      src={adminDirectoryPhotoUrl(id)}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="pt-6 text-sm text-gray-600 flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {driver.name}</span>
              <span>{driver.email}</span>
              {driver.slug && driver.directory_status === 'approved' && (
                <a href={`/drivers/${driver.slug}`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> View public profile
                </a>
              )}
            </div>

            {/* Gallery photos submitted for approval */}
            {Array.isArray(driver.gallery_photo_ids) && driver.gallery_photo_ids.length > 0 && (
              <div data-testid="directory-review-gallery">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Gallery photos ({driver.gallery_photo_ids.length})</h3>
                  <span className="text-xs text-gray-500">Click a photo to preview at full size</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {driver.gallery_photo_ids.map((pid) => (
                    <a
                      key={pid}
                      href={adminDirectoryGalleryUrl(id, pid)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative block aspect-square bg-gray-100 border border-gray-200 rounded overflow-hidden hover:ring-2 hover:ring-emerald-400"
                      data-testid={`directory-review-photo-${pid}`}
                    >
                      <img
                        src={adminDirectoryGalleryUrl(id, pid)}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="directory-review-diff">
              <DiffRow label="Business name" draft={draft.business_name} approved={approved.business_name} />
              <DiffRow label="Tagline" draft={draft.tagline} approved={approved.tagline} />
              <div className="md:col-span-2">
                <DiffRow label="Public bio" draft={draft.public_bio} approved={approved.public_bio} />
              </div>
              <DiffRow label="Services offered" draft={draft.services_offered} approved={approved.services_offered} />
              <DiffRow label="Vehicle" draft={draft.vehicle_description} approved={approved.vehicle_description} />
              <DiffRow label="Experience (years)" draft={draft.experience_years} approved={approved.experience_years} />
              <DiffRow label="Response time" draft={draft.response_time} approved={approved.response_time} />
              <DiffRow label="Primary colour" draft={draft.brand_primary_color} approved={approved.brand_primary_color} />
              <DiffRow label="Secondary colour" draft={draft.brand_secondary_color} approved={approved.brand_secondary_color} />
              <DiffRow label="Show phone" draft={draft.directory_show_phone ? draft.directory_phone : 'hidden'} approved={approved.directory_show_phone ? approved.directory_phone : 'hidden'} />
              <DiffRow label="Show WhatsApp" draft={draft.directory_show_whatsapp ? (draft.directory_whatsapp || draft.directory_phone) : 'hidden'} approved={approved.directory_show_whatsapp ? (approved.directory_whatsapp || approved.directory_phone) : 'hidden'} />
            </div>

            {driver.directory_rejection_reason && (
              <div className="text-sm border border-red-200 bg-red-50 rounded p-3 text-red-900">
                <span className="font-semibold">Previous admin note:</span> {driver.directory_rejection_reason}
              </div>
            )}
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={onClose} data-testid="directory-review-close">Close</Button>
            <Button
              onClick={() => setSuspendOpen(true)}
              disabled={busy || driver.directory_status === 'suspended'}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="directory-review-suspend"
            >
              <ShieldAlert className="w-4 h-4 mr-1.5" /> Suspend
            </Button>
            <Button
              onClick={() => setRejectOpen(true)}
              disabled={busy}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="directory-review-reject"
            >
              <X className="w-4 h-4 mr-1.5" /> Reject
            </Button>
            <Button
              onClick={() => doAction(() => adminApproveDirectory(id), 'Profile approved.')}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="directory-review-approve"
            >
              <Check className="w-4 h-4 mr-1.5" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject profile</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">The driver will see this message in their dashboard. Keep it constructive.</p>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Please add a professional profile photo and expand the bio…" data-testid="directory-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={() => { setRejectOpen(false); doAction(() => adminRejectDirectory(id, reason), 'Profile rejected.'); setReason(''); }} className="bg-red-600 hover:bg-red-700 text-white" data-testid="directory-reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend reason */}
      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Suspend profile</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">The profile will be immediately hidden from the public directory.</p>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for suspension (internal + shown to driver)…" data-testid="directory-suspend-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <Button onClick={() => { setSuspendOpen(false); doAction(() => adminSuspendDirectory(id, reason), 'Profile suspended.'); setReason(''); }} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="directory-suspend-confirm">Suspend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function AdminDirectoryApprovals() {
  const [statusTab, setStatusTab] = useState('pending');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, suspended: 0, draft: 0 });
  const [reviewId, setReviewId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { status: statusTab, limit: 100 };
      if (q.trim()) params.q = q.trim();
      const { data } = await adminListDirectoryApprovals(params);
      setRows(data.drivers || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error('Failed to load approvals.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [statusTab]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold font-['Inter']">Driver Directory Approvals</h2>
          <p className="text-sm text-gray-500">Approve, reject, or suspend public directory profiles. Independent of bidding.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Input placeholder="Search by name / email…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchData()} className="w-64" data-testid="directory-approvals-search" />
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={statusTab} onValueChange={setStatusTab}>
        <TabsList className="bg-gray-100 p-1 flex-wrap h-auto">
          {STATUSES.map((s) => (
            <TabsTrigger key={s.key} value={s.key} className="data-[state=active]:bg-white gap-2" data-testid={`directory-tab-${s.key}`}>
              {s.label}
              <span className="text-xs bg-gray-200 text-gray-700 rounded-full px-1.5 py-0.5 min-w-[1.5rem] text-center">{counts[s.key] ?? 0}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {STATUSES.map((s) => (
          <TabsContent key={s.key} value={s.key} className="mt-4">
            {loading ? (
              <div className="p-8 flex items-center gap-2 text-gray-500 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm" data-testid="directory-approvals-empty">No profiles in {s.label.toLowerCase()}.</div>
            ) : (
              <div className="border border-gray-200 rounded overflow-hidden bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left">
                      <th className="p-3 font-semibold">Business</th>
                      <th className="p-3 font-semibold">Email</th>
                      <th className="p-3 font-semibold">Status</th>
                      <th className="p-3 font-semibold">Submitted</th>
                      <th className="p-3 font-semibold">Reviewed</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`directory-approval-row-${d.id}`}>
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{d.business_name || d.name}</div>
                          <div className="text-xs text-gray-500">/drivers/{d.slug}</div>
                        </td>
                        <td className="p-3 text-gray-600">{d.email}</td>
                        <td className="p-3">
                          <StatusPill status={d.directory_status} />
                          {d.has_pending_changes && <div className="text-[10px] mt-1 text-amber-700 font-semibold">has pending changes</div>}
                        </td>
                        <td className="p-3 text-gray-500 text-xs">
                          {d.directory_submitted_at ? <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(d.directory_submitted_at).toLocaleString('en-IE')}</span> : '—'}
                        </td>
                        <td className="p-3 text-gray-500 text-xs">
                          {d.directory_reviewed_at ? new Date(d.directory_reviewed_at).toLocaleString('en-IE') : '—'}
                        </td>
                        <td className="p-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setReviewId(d.id)}
                            data-testid={`directory-approval-review-${d.id}`}
                          >
                            Review
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {reviewId && (
        <ReviewDialog
          id={reviewId}
          onClose={() => setReviewId(null)}
          onDone={() => { setReviewId(null); fetchData(); }}
        />
      )}
    </div>
  );
}
